import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import clsx from "clsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoice,
  faSyncAlt,
  faFileCsv,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";

const extractErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;

  if (!message) return fallback;
  if (typeof message === "string") return message;

  if (typeof message === "object") {
    const firstValue = Object.values(message)[0];
    if (Array.isArray(firstValue)) return firstValue[0] || fallback;
    if (typeof firstValue === "string") return firstValue;
  }

  return fallback;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
};

const StaleBadge = ({ stale }) => {
  const isStale = Number(stale) === 1;
  return (
    <span
      className={clsx(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold",
        isStale ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
      )}
    >
      {isStale ? "Yes" : "No"}
    </span>
  );
};

const normalizeDaysRemaining = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const DaysRemainingBadge = ({ stale, daysRemaining }) => {
  const isStale = Number(stale) === 1;
  const normalizedDays = normalizeDaysRemaining(daysRemaining);

  if (normalizedDays === null) {
    return (
      <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
        N/A
      </span>
    );
  }

  if (isStale) {
    return (
      <span className="inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
        Stale
      </span>
    );
  }

  if (normalizedDays === 0) {
    return (
      <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
        Due on received date
      </span>
    );
  }

  return (
    <span
      className={clsx(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold",
        normalizedDays <= 5 ? "bg-orange-100 text-orange-700" : "bg-sky-100 text-sky-700"
      )}
    >
      {normalizedDays} day{normalizedDays === 1 ? "" : "s"} left
    </span>
  );
};

const ClaimsStaleChecker = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [searchQuery, setSearchQuery] = useState("");
  const [staleFilter, setStaleFilter] = useState("ALL");
  const [loaDateFrom, setLoaDateFrom] = useState("");
  const [loaDateTo, setLoaDateTo] = useState("");
  const [deadlineFrom, setDeadlineFrom] = useState("");
  const [deadlineTo, setDeadlineTo] = useState("");

  const [sortBy, setSortBy] = useState("date_received");
  const [sortDirection, setSortDirection] = useState("desc");

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  };

  const fetchRecords = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${BASE_URL}/claims/stale-checker`, {
        headers: getAuthHeaders(),
        params: {
          limit_offset: (currentPage - 1) * itemsPerPage,
          limit: itemsPerPage,
          search_string: searchQuery.trim() || undefined,
          stale: staleFilter === "ALL" ? "ALL" : staleFilter,
          loa_date_issued_from: loaDateFrom || undefined,
          loa_date_issued_to: loaDateTo || undefined,
          stale_deadline_from: deadlineFrom || undefined,
          stale_deadline_to: deadlineTo || undefined,
          sort_by: sortBy,
          sort_direction: sortDirection,
        },
      });

      setRows(response.data?.data || []);
      setTotalCount(Number(response.data?.total_count || 0));
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to fetch stale checker records."));
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    staleFilter,
    loaDateFrom,
    loaDateTo,
    deadlineFrom,
    deadlineTo,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((previous) => (previous === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(column);
    setSortDirection("asc");
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStaleFilter("ALL");
    setLoaDateFrom("");
    setLoaDateTo("");
    setDeadlineFrom("");
    setDeadlineTo("");
    setCurrentPage(1);
  };

  const openViewModal = async (transNo) => {
    const encodedTransNo = encodeURIComponent(transNo);
    setViewModalOpen(true);
    setViewLoading(true);
    setViewRecord(null);

    try {
      const response = await axios.get(`${BASE_URL}/claims/stale-checker/${encodedTransNo}`, {
        headers: getAuthHeaders(),
      });
      setViewRecord(response.data?.data || null);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to load stale checker details."));
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleExport = async (format = "csv") => {
    try {
      const response = await axios.get(`${BASE_URL}/claims/stale-checker/export`, {
        headers: getAuthHeaders(),
        params: {
          search_string: searchQuery.trim() || undefined,
          stale: staleFilter === "ALL" ? "ALL" : staleFilter,
          loa_date_issued_from: loaDateFrom || undefined,
          loa_date_issued_to: loaDateTo || undefined,
          stale_deadline_from: deadlineFrom || undefined,
          stale_deadline_to: deadlineTo || undefined,
          sort_by: sortBy,
          sort_direction: sortDirection,
          format,
        },
        responseType: "blob",
      });

      const disposition = response.headers?.["content-disposition"] || "";
      const matchedFileName = disposition.match(/filename="?([^\"]+)"?/i);
      const fallback = `claims_stale_checker.${format === "excel" ? "xls" : "csv"}`;
      const fileName = matchedFileName?.[1] || fallback;

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${format === "excel" ? "Excel" : "CSV"} successfully.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to export stale checker records."));
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1700px] p-4">
      <ToastContainer />

      <div className="mb-4 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-col gap-3 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-4 py-4 text-white md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white/15 p-2 ring-1 ring-white/30">
              <FontAwesomeIcon icon={faFileInvoice} className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">Claims Stale Checker</h1>
              <p className="text-xs text-white/90">
                {totalCount.toLocaleString()} total record{totalCount === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={fetchRecords}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/25 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faSyncAlt} />
              Refresh
            </button>

            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/25 hover:bg-white/20"
            >
              Clear Filters
            </button>

            <button
              type="button"
              onClick={() => handleExport("csv")}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/25 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faFileCsv} />
              Export CSV
            </button>

            <button
              type="button"
              onClick={() => handleExport("excel")}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/25 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faFileExcel} />
              Export Excel
            </button>


          </div>
        </div>

        <div className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">Important Note</p>
          <p className="mt-1">
            Stale detection follows this formula: <span className="font-semibold">Stale Deadline = LOA Date Issued + 5 days grace period + Provider Submission of Claims (Contract) Days</span>.
          </p>
          <p className="mt-1 text-xs text-amber-800">
            The contract days are dynamic and pulled from Provider settings; this checker is for viewing/confirmation only.
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Days Remaining and stale status are computed using the latest Date Received per transaction (with grace period included).
          </p>
        </div>

        <div className="grid gap-3 p-4 md:grid-cols-2 lg:grid-cols-6">
          <div className="relative lg:col-span-2">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search LOA, TRN, batch, provider..."
              className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <select
            value={staleFilter}
            onChange={(event) => {
              setStaleFilter(event.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          >
            <option value="ALL">All Stale Status</option>
            <option value="1">Yes (Stale)</option>
            <option value="0">No (Not Stale)</option>
          </select>

          <div className="space-y-1">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              LOA Date From
            </label>
            <input
              type="date"
              value={loaDateFrom}
              onChange={(event) => {
                setLoaDateFrom(event.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              title="LOA Date Issued From"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              LOA Date To
            </label>
            <input
              type="date"
              value={loaDateTo}
              onChange={(event) => {
                setLoaDateTo(event.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              title="LOA Date Issued To"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Stale Deadline From
            </label>
            <input
              type="date"
              value={deadlineFrom}
              onChange={(event) => {
                setDeadlineFrom(event.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              title="Stale Deadline From"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Stale Deadline To
            </label>
            <input
              type="date"
              value={deadlineTo}
              onChange={(event) => {
                setDeadlineTo(event.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              title="Stale Deadline To"
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="max-h-[65vh] overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                {["batch_no", "trans_no", "loa_no", "provider_code", "provider_name", "loa_date_issued", "contract_days", "grace_days", "date_received", "stale_deadline", "days_remaining", "stale"].map((column) => {
                  const labels = {
                    batch_no: "Batch No",
                    trans_no: "TRN#",
                    loa_no: "LOA#",
                    provider_code: "Provider Code",
                    provider_name: "Provider Name",
                    loa_date_issued: "LOA Date Issued",
                    contract_days: "Contract Days",
                    grace_days: "Grace Days",
                    date_received: "Date Received",
                    stale_deadline: "Stale Deadline",
                    days_remaining: "Days Remaining",
                    stale: "Is Stale",
                  };

                  const sortable = column !== "grace_days";

                  return (
                    <th
                      key={column}
                      onClick={() => sortable && handleSort(column)}
                      className={clsx(
                        "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                        sortable && "cursor-pointer select-none"
                      )}
                    >
                      {labels[column]}{" "}
                      {sortBy === column ? (sortDirection === "asc" ? "[ASC]" : "[DESC]") : ""}
                    </th>
                  );
                })}
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading
                ? Array.from({ length: itemsPerPage }).map((_, index) => (
                    <tr key={`skeleton-${index}`} className="animate-pulse">
                      {Array.from({ length: 13 }).map((__, cellIndex) => (
                        <td key={cellIndex} className="px-4 py-3">
                          <div className="h-4 rounded bg-gray-200" />
                        </td>
                      ))}
                    </tr>
                  ))
                : rows.length === 0
                ? (
                  <tr>
                    <td colSpan={13} className="px-4 py-16 text-center text-sm text-gray-500">
                      No stale checker records found for the selected filters.
                    </td>
                  </tr>
                  )
                : rows.map((row) => (
                    <tr key={`${row.trans_no}-${row.loa_no}`} className="transition-colors hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-800">{row.batch_no || "-"}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.trans_no || "-"}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.loa_no || "-"}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{row.provider_code || "-"}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{row.provider_name || "-"}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{formatDate(row.loa_date_issued)}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{row.contract_days ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{row.grace_days ?? 5}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{formatDate(row.date_received)}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">{formatDate(row.stale_deadline)}</td>
                      <td className="px-4 py-3 text-sm text-gray-800">
                        <DaysRemainingBadge stale={row.stale} daysRemaining={row.days_remaining} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800"><StaleBadge stale={row.stale} /></td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          type="button"
                          title="View"
                          onClick={() => openViewModal(row.trans_no)}
                          className="rounded-md p-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
          <div className="text-xs text-gray-600">
            Showing {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount.toLocaleString()} entries
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              <ChevronDoubleLeftIcon className="h-5 w-5" />
              First
            </button>

            <button
              type="button"
              onClick={() => setCurrentPage((previous) => Math.max(1, previous - 1))}
              disabled={currentPage === 1}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              <ChevronLeftIcon className="h-5 w-5" />
              Previous
            </button>

            {getPageNumbers().map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={clsx(
                  "rounded-md px-2 py-1 text-xs",
                  currentPage === page ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                )}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setCurrentPage((previous) => Math.min(totalPages, previous + 1))}
              disabled={currentPage === totalPages}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === totalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              Next
              <ChevronRightIcon className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className={clsx(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                currentPage === totalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              Last
              <ChevronDoubleRightIcon className="h-5 w-5" />
            </button>

            <select
              value={itemsPerPage}
              onChange={(event) => {
                setItemsPerPage(Number(event.target.value));
                setCurrentPage(1);
              }}
              className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>{size} per page</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {viewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 text-white">
              <h2 className="text-base font-semibold">Stale Checker Details</h2>
              <button
                type="button"
                onClick={() => setViewModalOpen(false)}
                className="text-white/90 hover:text-white"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 p-5 text-sm">
              {viewLoading ? (
                <div className="space-y-2">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div><span className="font-semibold text-gray-700">Batch No:</span> {viewRecord?.batch_no || "-"}</div>
                    <div><span className="font-semibold text-gray-700">TRN#:</span> {viewRecord?.trans_no || "-"}</div>
                    <div><span className="font-semibold text-gray-700">LOA#:</span> {viewRecord?.loa_no || "-"}</div>
                    <div><span className="font-semibold text-gray-700">Provider Code:</span> {viewRecord?.provider_code || "-"}</div>
                    <div className="md:col-span-2"><span className="font-semibold text-gray-700">Provider Name:</span> {viewRecord?.provider_name || "-"}</div>
                    <div><span className="font-semibold text-gray-700">LOA Date Issued:</span> {formatDate(viewRecord?.loa_date_issued)}</div>
                    <div><span className="font-semibold text-gray-700">Stale Deadline:</span> {formatDate(viewRecord?.stale_deadline)}</div>
                    <div><span className="font-semibold text-gray-700">Contract Days:</span> {viewRecord?.contract_days ?? 0}</div>
                    <div><span className="font-semibold text-gray-700">Grace Days:</span> {viewRecord?.grace_days ?? 5}</div>
                    <div><span className="font-semibold text-gray-700">Date Received:</span> {formatDate(viewRecord?.date_received)}</div>
                    <div className="flex items-center gap-2"><span className="font-semibold text-gray-700">Days Remaining:</span> <DaysRemainingBadge stale={viewRecord?.stale} daysRemaining={viewRecord?.days_remaining} /></div>
                    <div className="flex items-center gap-2"><span className="font-semibold text-gray-700">Is Stale:</span> <StaleBadge stale={viewRecord?.stale} /></div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsStaleChecker;


